import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, HostListener, ChangeDetectionStrategy } from '@angular/core';
import { finalize, flatMap, takeUntil, tap } from 'rxjs/operators';
import { ServiceaftaleDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/serviceaftale-dto';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { LoaderService } from 'src/app/services/loader.service';
import { OpfoelgningService } from 'src/app/api/services';
import { defer, Subject } from 'rxjs';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { AuthService } from 'src/app/services/auth.service';
import { EGIS_SERVICE_CALLER } from 'src/app/constants';
import { OpfoelgningAction, OpfoelgningDialogData, OpfoelgningDialogResponse } from 'src/app/components/aftale/models';
import { OpfoelgningDialogComponent } from '../opfoelgning-dialog/opfoelgning-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { OpfoelgningWithServiceaftaleDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/opfoelgning-with-serviceaftale-dto';
import moment from 'moment';
import { SignalrService } from 'src/app/services/signalr.service';
import { NotificationType } from 'src/app/notifications/models/notification-models';
import { NotificationService } from 'src/app/notifications/services/notification.service';
import { OpfoelgningDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/opfoelgning-dto';
import { PaginationCacheService } from 'src/app/services/paginationcache.service';
import { GetServiceaftaleOpfoelgningerResponse } from 'src/app/api/models/OK/Egis/Service/Models/Response/get-serviceaftale-opfoelgninger-response';
import { inOutAnimation } from 'src/app/common/animations';
import { ServiceaftaleStateEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/serviceaftale-state-enum';

@Component({
  selector: 'app-opfoelgning-serviceaftale',
  templateUrl: './opfoelgning-serviceaftale.component.html',
  styleUrls: ['./opfoelgning-serviceaftale.component.scss'],
  animations: [inOutAnimation],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class OpfoelgningServiceaftaleComponent implements OnInit, OnDestroy, AfterViewInit {
  canWrite: boolean = false;
  canSeeAllUsersOpfoelgninger: boolean = false;

  quickViewOpen: boolean = false;
  globalActiveRowID: string = '';
  selectedaftale: ServiceaftaleDto;

  opfoelgninger: OpfoelgningDto[] = [];
  rowCount: number;
  pageCount: number;

  displayedColumns: string[] = [
    'serviceaftale.serviceAbonnement.produkt.id',
    'opfoelgning',
    'ServiceaftaleState',
    'priority',
    'deadline',
    'user',
    'serviceaftale.okKundeAftaleNr',
    'adresse',
    'telefon',
    'modifiedOn',
    'createdOn',
  ];

  showForAllUsers: boolean = false;

  unsubscribe$ = new Subject<void>();

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      if (this.quickViewOpen) {
        this.closeQuickView();
      }
    }
  }

  dataSource: MatTableDataSource<OpfoelgningWithServiceaftaleDto> = new MatTableDataSource<OpfoelgningWithServiceaftaleDto>();

  constructor(
    private opfoelgningService: OpfoelgningService,
    private router: Router,
    private route: ActivatedRoute,
    private loader: LoaderService,
    private errorHandler: ErrorHandlerService,
    private auth: AuthService,
    private signalR: SignalrService,
    private dialog: MatDialog,
    private notification: NotificationService,
    private paginationCache: PaginationCacheService,
  ) {
    this.dataSource = new MatTableDataSource();
    this.canWrite = this.auth.canWrite;
    this.canSeeAllUsersOpfoelgninger = this.auth.canRead;
  }

  @ViewChild(MatSort) sort: MatSort;

  ngOnInit(): void {
    this.route.queryParams
      .pipe(
        flatMap((queryParams: Params) => {
          const showForAllUsers = queryParams['showForAllUsers'];
          const orderBy = queryParams['orderBy'];
          const orderDir = queryParams['orderDir'];
          if (showForAllUsers) {
            this.showForAllUsers = JSON.parse(showForAllUsers);
          }
          if (orderBy && orderBy !== '') {
            this.orderBy = orderBy;
          }

          if (orderDir && orderDir !== '') {
            this.orderDir = orderDir;
          }
          this.resetDataAndPagination();
          const pagination = this.paginationCache.position;
          if (pagination) {
            this.page = Number(pagination.page);
            this.prevRow = pagination.id;
          }
          return this.getServiceaftaleOpfoelgninger$.pipe(
            tap((response: GetServiceaftaleOpfoelgningerResponse) => {
              this.skip = this.page - 1;
            }),
          );
        }),
      )
      .subscribe(
        () => {},
        (error) => {
          this.errorHandler.HandleError(error);
        },
      );

    this.signalR.opfoelgningChanged$
      .pipe(
        flatMap(() => {
          this.resetDataAndPagination();
          return this.getServiceaftaleOpfoelgninger$;
        }),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(
        () => {},
        (error) => {
          this.errorHandler.HandleError(error);
        },
      );

    this.loader.loadingStateChanged$.subscribe((isLoading) => {
      this.isLoading = isLoading;
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
  }

  getServiceaftaleOpfoelgninger$ = defer(() => {
    this.loader.show();
    let top = this.page - this.skip;
    let skip = this.skip > this.page ? top - 1 : this.skip;
    return this.opfoelgningService
      .getServiceaftaleOpfoelgninger({
        body: {
          showForAllUsers: this.showForAllUsers,
          pageTop: top < 1 ? 1 : top,
          pageSkip: skip < 0 ? 0 : skip,
          orderBy: this.orderBy,
          orderDir: this.orderDir,
        },
        caller: EGIS_SERVICE_CALLER,
      })
      .pipe(
        tap((response) => {
          this.rowCount = response.rowCount;
          this.pageCount = response.pageCount;
          this.opfoelgninger = this.opfoelgninger.concat(response.opfoelgninger);
          this.dataSource = new MatTableDataSource(this.opfoelgninger);
        }),
        finalize(() => {
          this.loader.hide();
        }),
        takeUntil(this.unsubscribe$),
      );
  });

  onRowClick(row: any) {
    this.globalActiveRowID = row.id;
    this.quickViewOpen = true;
    this.selectedaftale = row.serviceaftale;
  }

  onRowDblClick(selectedaftale: ServiceaftaleDto) {
    this.router.navigate(['/aftale', selectedaftale.okKundeAftaleNr, { page: this.page }]);
  }

  getProductIcon(serviceAftale: ServiceaftaleDto) {
    return serviceAftale && serviceAftale.serviceAbonnement && serviceAftale.serviceAbonnement.product
      ? serviceAftale.serviceAbonnement.product.text.toLowerCase()
      : '';
  }

  getStatusIcon(serviceAftale: ServiceaftaleDto) {
    if (serviceAftale.isActive) return 'checkmark';
    //Created-pending vs notactive
    return serviceAftale.serviceaftaleState.state == ServiceaftaleStateEnum.CreatedPending ? 'watch' : 'ban';
  }

  closeQuickView() {
    this.quickViewOpen = false;
    this.globalActiveRowID = '';
  }

  opfoelgningClick(event: Event, opfoelgning: OpfoelgningWithServiceaftaleDto) {
    event.stopPropagation();

    const data: OpfoelgningDialogData = {
      entityNo: opfoelgning.serviceaftale.serviceAftaleNo,
      opfoelgninger: opfoelgning.serviceaftale.opfoelgningList,
      okKundeAftaleNr: opfoelgning.serviceaftale.okKundeAftaleNr,
    };

    let dialogRef = this.dialog.open(OpfoelgningDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      data,
    });

    dialogRef.afterClosed().subscribe((response: OpfoelgningDialogResponse) => {
      if (response) {
        this.onOpfoelgningChanged(response);
      }
    });
  }

  deadline(opfoelgning: OpfoelgningWithServiceaftaleDto) {
    const daysToDeadline = moment(opfoelgning.deadline).endOf('day').diff(moment(), 'days');
    if (daysToDeadline <= 0) {
      return 'attention-box attention-box--red';
    } else if (daysToDeadline <= 7) {
      return 'attention-box attention-box--yellow';
    }
    return '';
  }

  onOpfoelgningChanged(response: OpfoelgningDialogResponse) {
    if (response.action == OpfoelgningAction.Upsert) {
      this.loader.show();

      this.opfoelgningService
        .upsertServiceaftaleOpfoelgning({
          caller: EGIS_SERVICE_CALLER,
          body: {
            id: response.opfoelgning.id,
            note: response.opfoelgning.note,
            deadline: response.opfoelgning.deadline ? new Date(response.opfoelgning.deadline).toOkDateString() : null,
            prioritet: response.opfoelgning.priority,
            entityNo: response.opfoelgning.entityNo,
          },
        })
        .pipe(
          finalize(() => {
            this.loader.hide();
          }),
          takeUntil(this.unsubscribe$),
        )
        .subscribe(
          () => {
            this.notification.success('Opfølgning er gemt.');
          },
          (error) => {
            this.errorHandler.HandleError(error);
          },
        );
    } else if (response.action == OpfoelgningAction.Delete) {
      this.opfoelgningService
        .removeServiceaftaleOpfoelgning({ caller: EGIS_SERVICE_CALLER, id: response.opfoelgning.id })
        .pipe(
          finalize(() => {
            this.loader.hide();
          }),
          takeUntil(this.unsubscribe$),
        )
        .subscribe(
          () => {
            this.notification.success('Opfølgning er fjernet.');
          },
          (error) => {
            this.errorHandler.HandleError(error);
          },
        );
    }
  }

  onShowForAllUsersToggle() {
    this.resetDataAndPagination();
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { showForAllUsers: this.showForAllUsers },
      queryParamsHandling: 'merge',
    });
  }

  more() {
    this.page += 1;
    this.skip += 1;
    this.loader.show();
    (this.getServiceaftaleOpfoelgninger$
      .pipe(
        finalize(() => {
          this.loader.hide();
        }),
      )
      .subscribe(),
      (error) => {
        this.errorHandler.HandleError(error);
        this.page -= 1;
      });
  }

  sortData(sort: Sort) {
    this.resetDataAndPagination();

    const orderParams: Params = {
      orderBy: sort.active ? sort.active : null,
      orderDir: sort.direction ? sort.direction : null,
    };

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: orderParams,
      queryParamsHandling: 'merge',
    });
  }

  resetDataAndPagination() {
    this.opfoelgninger = [];
    this.skip = 0;
    this.page = 1;
    this.prevRow = null;
  }

  skip: number = 0;
  page: number = 1;
  orderBy: 'modifiedOn';
  orderDir: 'desc';
  prevRow: number = null;
  isLoading: boolean = false;
  highLight(id: number) {
    const row: any = this.opfoelgninger.find((p: any) => p.serviceaftale.okKundeAftaleNr == id);
    if (row) this.globalActiveRowID = row.serviceaftale.id.toString();
  }
}
